import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCreateDeal } from "@/hooks/deals/useDeals";
import { useQueryClient } from "@tanstack/react-query";
import { useLeadById, type LeadSearchResult } from "@/hooks/leads/useLeadSearch";
import { useCompanyById, type CompanySearchResult } from "@/hooks/companies/useCompanySearch";
import { DealForm } from "./DealForm";
import { LeadStep } from "./wizard/LeadStep";
import { CompanyStep } from "./wizard/CompanyStep";

const FORM_ID = "deal-create-form";

interface DealCreateWizardProps {
  defaultPipelineId?: string;
  defaultStageId?: string;
  defaultPersonId?: string;
  defaultOrganizationId?: string;
  defaultOwnerId?: string;
  onCreated?: (createdDealId: string | undefined) => void;
  onClose: () => void;
}

function mapLeadCompany(
  companies: LeadSearchResult["companies"],
): CompanySearchResult | null {
  if (!companies) return null;
  return {
    id: companies.id,
    name: companies.name,
    website: companies.website,
    industry: companies.industry,
  };
}

export function DealCreateWizard({
  defaultPipelineId,
  defaultStageId,
  defaultPersonId,
  defaultOrganizationId,
  defaultOwnerId,
  onCreated,
  onClose,
}: DealCreateWizardProps) {
  const createDeal = useCreateDeal();
  const queryClient = useQueryClient();

  // Contextos com lead/empresa pré-definidos abrem direto na etapa do negócio.
  const startsAtDeal = !!defaultPersonId || !!defaultOrganizationId;
  const [step, setStep] = useState<number>(startsAtDeal ? 3 : 1);
  const [maxStep, setMaxStep] = useState<number>(startsAtDeal ? 3 : 1);

  const [selectedLead, setSelectedLead] = useState<LeadSearchResult | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchResult | null>(null);
  const [companyInherited, setCompanyInherited] = useState(false);
  const [companyTouched, setCompanyTouched] = useState(false);

  // Pré-preenchimento a partir do contexto de abertura
  const leadByIdQuery = useLeadById(defaultPersonId);
  const prefillLead = leadByIdQuery.data;
  const { data: prefillCompany } = useCompanyById(
    defaultOrganizationId && !prefillLead?.companies ? defaultOrganizationId : undefined,
  );

  useEffect(() => {
    if (prefillLead && !selectedLead) {
      setSelectedLead(prefillLead);
      if (prefillLead.companies) {
        setSelectedCompany(mapLeadCompany(prefillLead.companies));
        setCompanyInherited(true);
      }
    }
  }, [prefillLead, selectedLead]);

  useEffect(() => {
    if (prefillCompany && !selectedCompany && !companyTouched) {
      setSelectedCompany(prefillCompany);
      setCompanyInherited(false);
    }
  }, [prefillCompany, selectedCompany, companyTouched]);

  const go = (n: number) => {
    setStep(n);
    setMaxStep((m) => Math.max(m, n));
  };

  const handleLeadSelect = (lead: LeadSearchResult | null) => {
    setSelectedLead(lead);
    // Empresa herda do lead enquanto o usuário não a definir manualmente.
    if (!companyTouched) {
      if (lead?.companies) {
        setSelectedCompany(mapLeadCompany(lead.companies));
        setCompanyInherited(true);
      } else {
        setSelectedCompany(null);
        setCompanyInherited(false);
      }
    }
  };

  const handleCompanySelect = (company: CompanySearchResult | null) => {
    setSelectedCompany(company);
    setCompanyInherited(false);
    setCompanyTouched(true);
  };

  // Aguarda o lead pré-definido terminar de carregar (sucesso OU erro) antes de
  // montar a etapa do negócio, para o título sugerido já refletir lead/empresa
  // sem travar o fluxo caso o lead não exista/não seja visível.
  const prefillResolved = !defaultPersonId || !leadByIdQuery.isLoading;
  const dealStepMounted = maxStep >= 3 && prefillResolved;

  // Inclui fallback para os dados do contexto (prefill) porque o DealForm pode
  // montar no mesmo render em que a query do lead resolve, antes do state commitar.
  const suggestedName =
    selectedCompany?.name ||
    selectedLead?.name ||
    prefillLead?.companies?.name ||
    prefillLead?.name ||
    prefillCompany?.name;
  const suggestedTitle = suggestedName ? `Negócio - ${suggestedName}` : undefined;

  const handleDealSubmit = async (dealData: Record<string, unknown>) => {
    try {
      const payload = {
        ...dealData,
        pipeline_id: (dealData.pipeline_id as string) || defaultPipelineId,
        lead_id: selectedLead?.id ?? null,
        company_id: selectedCompany?.id ?? null,
        ...(defaultOwnerId && !dealData.owner_id ? { owner_id: defaultOwnerId } : {}),
      } as Parameters<typeof createDeal.mutateAsync>[0];

      const created = await createDeal.mutateAsync(payload);

      queryClient.invalidateQueries({ queryKey: ["deals-by-stage"] });
      queryClient.invalidateQueries({ queryKey: ["deal-stage-counts"] });

      onCreated?.((created as { id?: string } | undefined)?.id);
      onClose();
    } catch {
      // Erro tratado pelo toast da mutation
    }
  };

  return (
    <>
      <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
        <DialogTitle>Novo Negócio</DialogTitle>
        <DialogDescription>
          {step === 1
            ? "Selecione ou crie o lead deste negócio."
            : step === 2
              ? "Vincule uma empresa (opcional)."
              : "Preencha os dados do negócio."}
        </DialogDescription>
      </DialogHeader>

      <Stepper step={step} />

      <div className="overflow-y-auto flex-1 px-6 pb-2 pt-1">
        {step === 1 && <LeadStep selectedLead={selectedLead} onSelect={handleLeadSelect} />}

        {step === 2 && (
          <CompanyStep
            selectedCompany={selectedCompany}
            inherited={companyInherited}
            onSelect={handleCompanySelect}
          />
        )}

        {dealStepMounted && (
          <div className={step === 3 ? "space-y-4" : "hidden"}>
            <DealSummary
              lead={selectedLead}
              company={selectedCompany}
              onEditLead={() => go(1)}
              onEditCompany={() => go(2)}
            />
            <DealForm
              variant="modal"
              formId={FORM_ID}
              hideRelationFields
              defaultPipelineId={defaultPipelineId}
              defaultStageId={defaultStageId}
              defaultOwnerId={defaultOwnerId}
              defaultTitle={suggestedTitle}
              onSubmit={handleDealSubmit}
              onCancel={onClose}
              isLoading={createDeal.isPending}
            />
          </div>
        )}

        {step === 3 && !dealStepMounted && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Carregando...
          </div>
        )}
      </div>

      <DialogFooter className="px-6 py-4 border-t flex-shrink-0 sm:justify-between">
        {step === 1 && (
          <>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => go(2)}>
              {selectedLead ? "Avançar" : "Continuar sem lead"}
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Button type="button" variant="ghost" onClick={() => go(1)}>
              Voltar
            </Button>
            <Button type="button" onClick={() => go(3)}>
              {selectedCompany ? "Avançar" : "Continuar sem empresa"}
            </Button>
          </>
        )}

        {step === 3 && (
          <>
            <Button type="button" variant="ghost" onClick={() => go(2)}>
              Voltar
            </Button>
            <Button type="submit" form={FORM_ID} disabled={createDeal.isPending}>
              {createDeal.isPending ? "Criando..." : "Criar Negócio"}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
}

function Stepper({ step }: { step: number }) {
  const items = ["Lead", "Empresa", "Negócio"];
  return (
    <div className="flex items-center gap-2 px-6 pb-2 flex-shrink-0">
      {items.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  "h-5 w-5 rounded-full text-2xs flex items-center justify-center font-semibold flex-shrink-0",
                  active
                    ? "bg-primary text-primary-foreground"
                    : done
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground",
                )}
              >
                {done ? <Check className="h-3 w-3" /> : n}
              </span>
              <span className={cn("text-xs", active ? "font-medium text-foreground" : "text-muted-foreground")}>
                {label}
              </span>
            </div>
            {n < 3 && <div className="h-px w-6 bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

function DealSummary({
  lead,
  company,
  onEditLead,
  onEditCompany,
}: {
  lead: LeadSearchResult | null;
  company: CompanySearchResult | null;
  onEditLead: () => void;
  onEditCompany: () => void;
}) {
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-muted-foreground">Lead:</span>
        <span className="font-medium">{lead?.name ?? "—"}</span>
        <button type="button" onClick={onEditLead} className="text-2xs text-primary hover:underline">
          editar
        </button>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-muted-foreground">Empresa:</span>
        <span className="font-medium">{company?.name ?? "—"}</span>
        <button type="button" onClick={onEditCompany} className="text-2xs text-primary hover:underline">
          editar
        </button>
      </span>
    </div>
  );
}
